import { invariant } from '../util/invariant';
const MPEGTS_PACKET_SIZE = 188;

/**
 * Rewrite the MPEG TS packets in the bytes array to ignore all audio streams
 * except the given audio stream index.
 *
 * Audio streams is zero index in the order which ADTS streams are described in
 * the Program Map Table (PMT).
 *
 * While arbitrary MPEG TS packets can have Program Association Tables (PAT) and
 * Program Map Tables (PMT) that are so large that they need to be split across
 * multipe MPEG TS packets, this implementation assumes for the sake of
 * simplicity that they are small enough to fit in a single packet.
 *
 * Also for sake of simplicity this function does not rewrite the CRC_32
 * checksum of MPEG TS packets it rewrites, since Mux.js ignores the CRC_32
 * fields.
 *
 * The more complete rawDumpTs.ts parser was stripped of everything
 * non-essential to make this low-overhead MPEG TS parser and rewriter which
 * does the minimal amount of work needed to locate the bytes that needs to be
 * modified to cause Mux.js to ignore specific audio streams.
 *
 * The MPEG TS packet format we are parsing and rewriting is described in
 * https://web.archive.org/web/20230703115812if_/https://www.itu.int/rec/dologin_pub.asp?lang=f&id=T-REC-H.222.0-199507-S%21%21PDF-E
 *
 * In addition to modifying the given byte array, the function returns the total
 * number of audio streams detected. I.e. if this function returns 8, then 0--7
 * is the valid range for the audioStreamIndexToKeep parameter.
 */
export function soloAudioStream(bytes: Uint8Array, audioStreamIndexToKeep: number): number | null {
  let programMapPid = NaN;
  let packetStart = 0;
  let audioStreamCount: number | null = null;
  let pmsSpans: SpanList | null = null;
  while (true) {
    let pos = packetStart;
    packetStart += MPEGTS_PACKET_SIZE;
    if (packetStart > bytes.byteLength) {
      break;
    }

    invariant(bytes[pos] == 0x47, 'Expected MPEG TS sync byte');

    const pid = readPid(bytes, pos + 1);
    if (pid != 0x00 /* PID_Program_Association_Table */ && pid != programMapPid) {
      /*
        We only bother parsing more of the packet if the packet program ID is
        one of the two IDs we care about.

        We care about PID_Program_Association_Table, because this packet tells
        use what the PID of the Program Map Table (PMT) is, and we care about
        the the PMT because this packet contains the audio stream descriptions
        we need to rewrite.
      */
      continue;
    }

    /*
      A couple of bits in the packet header tells us whether there exists fields
      that specify additional bytes to skip before reaching the packet payload
      which we actually care about.
    */

    // eslint-disable-next-line no-bitwise
    const payload_unit_start_indicator = bytes[pos + 1] & 0x40;

    /* Upper bit of the adaptation_field_control */
    // eslint-disable-next-line no-bitwise
    const hasAdaptationField = bytes[pos + 3] & 0x20;

    /*
      Skip past the mandatory four byte header containing sync_byte,
      transport_error_indicator, payload_unit_start_indicator,
      transport_priority, PID, transport_scrambling_control,
      adaptation_field_control, and continuity_counter.
    */
    pos += 4;

    if (hasAdaptationField) {
      // Skip adaptation fields
      const adaptation_field_length = bytes[pos];
      pos += 1 + adaptation_field_length;
    }

    let pointer_field = 0;
    if (payload_unit_start_indicator) {
      // Skip to start of payload
      pointer_field = bytes[pos];
      pos += 1 + pointer_field;
    }

    if (pid == 0x00 /* PID_Program_Association_Table */) {
      programMapPid = readProgramAssociationSection(bytes, pos);
    } else {
      invariant(pointer_field === 0, 'Non-zero pointer_field for Program Map Section not supported, ' + pointer_field);

      const size = packetStart - pos;
      if (payload_unit_start_indicator) {
        pmsSpans = {
          combinedSize: size,
          offsets: [pos],
          sizes: [size]
        };
      } else {
        invariant(pmsSpans, 'Unexpected Program Map Section span');
        pmsSpans.combinedSize += size,
        pmsSpans.offsets.push(pos);
        pmsSpans.sizes.push(size);
      }
      const asCount = patchProgramMapSection(bytes, audioStreamIndexToKeep, pmsSpans);
      if (asCount !== null) {
        audioStreamCount = asCount;
      }
    }
  }

  return audioStreamCount;
}

function readCrcPosFromSectionHeader(bytes: Uint8Array, pos: number) {
  // eslint-disable-next-line no-bitwise
  const section_length = ((bytes[pos + 1] & 0x0f) << 8) | bytes[pos + 2];
  /* +3 for position after section_length field, and -4 for position before crc field */
  return pos + section_length - 1;
}

/**
 * A list of byte spans that should be parsed as a single unit. We use this to
 * describe where the underlying bytes of the Program Map Section that we wish
 * to modify is located. We refer to the underlying bytes instead of copying the
 * bytes of the spans into a single flat buffer, since our goal is to patch the
 * Program Map Section in-place in order to mute certain audio streams.
 */
interface SpanList {
  combinedSize: number;
  offsets: number[];
  sizes: number[];
}

interface SpanPos {
  spanIndex: number;
  byteOffset: number;
}

function advanceSpanPos(spanList: SpanList, mutPos: SpanPos, byteCount: number) {
  mutPos.byteOffset += byteCount;
  while (mutPos.spanIndex < spanList.sizes.length && mutPos.byteOffset >= spanList.sizes[mutPos.spanIndex]) {
    mutPos.byteOffset -= spanList.sizes[mutPos.spanIndex];
    mutPos.spanIndex += 1;
  }
}

function spanEof(spanList: SpanList, pos: SpanPos) {
  return pos.spanIndex >= spanList.offsets.length;
}

function spanBefore(posA: SpanPos, posB: SpanPos) {
  if (posA.spanIndex < posB.spanIndex) {
    return true;
  }

  if (posA.spanIndex > posB.spanIndex) {
    return false;
  }

  return posA.byteOffset < posB.byteOffset;
}

function readSpanBEU16(bytes: Uint8Array, spanList: SpanList, mutPos: SpanPos): number | null {
  const high = readSpanU8(bytes, spanList, mutPos);
  if (high === null) {
    return null;
  }
  const low = readSpanU8(bytes, spanList, mutPos);
  if (low === null) {
    return null;
  }
  // eslint-disable-next-line no-bitwise
  return (high << 8) | low;
}

function readSpanU8(bytes: Uint8Array, spanList: SpanList, mutPos: SpanPos): number | null {
  if (spanEof(spanList, mutPos)) {
    return null;
  }

  const val = bytes[spanList.offsets[mutPos.spanIndex] + mutPos.byteOffset];
  advanceSpanPos(spanList, mutPos, 1);
  return val;
}

function readPid(bytes: Uint8Array, pos: number) {
  // eslint-disable-next-line no-bitwise
  return ((bytes[pos] & 0x1f) << 8) | bytes[pos + 1];
}

function readProgramAssociationSection(bytes: Uint8Array, pos: number): number {
  const crcPos = readCrcPosFromSectionHeader(bytes, pos);
  /* skip past section header */
  pos += 8;

  let programMapPid = NaN;

  while (pos < crcPos) {
    // eslint-disable-next-line no-bitwise
    const hasProgramMapPid = bytes[pos] || bytes[pos + 1];
    if (hasProgramMapPid) {
      // eslint-disable-next-line no-bitwise
      programMapPid = readPid(bytes, pos + 2);
    }
    pos += 4;
  }
  return programMapPid;
}

/**
 * Returns number of audio channels seen, or null if the program map section
 * described by the spanList is incomplete.
 */
function patchProgramMapSection(bytes: Uint8Array, audioStreamIndexToKeep: number, spanList: SpanList): number | null {
  const spanPos: SpanPos = {
    spanIndex: 0,
    byteOffset: 0
  };
  advanceSpanPos(spanList, spanPos, 1);
  if (spanEof(spanList, spanPos)) {
    return null;
  }

  const sectionLengthBe16 = readSpanBEU16(bytes, spanList, spanPos);
  if (sectionLengthBe16 === null) {
    return null;
  }

  // eslint-disable-next-line no-bitwise
  const section_length = sectionLengthBe16 & 0x0FFF;
  if (section_length > spanList.combinedSize) {
    /*
      We haven't collected all of the byte spans from the transport stream
      packets that make up the program map section yet. We expect to be called
      with a more complete spanList later on.
    */
    return null;
  }

  const crcSpanPos = { spanIndex: spanPos.spanIndex, byteOffset: spanPos.byteOffset };
  advanceSpanPos(spanList, crcSpanPos, section_length - 4);


  /* skip past section header and past PCR_PID and reserved fields */
  advanceSpanPos(spanList, spanPos, 7);

  // Skip over program association section descriptors
  const programInfoLengthBE = readSpanBEU16(bytes, spanList, spanPos);
  invariant(programInfoLengthBE !== null, 'Unexpected end of section before program_info_length');
  // eslint-disable-next-line no-bitwise
  const program_info_length = programInfoLengthBE & 0x0FFF;

  advanceSpanPos(spanList, spanPos, program_info_length);

  let audioIndex = 0;

  /*
    Arbitrary number in the user private stream type assignments range
    0x80 to 0xFF.

    See Table 2-29 of
    https://www.ic.tu-berlin.de/fileadmin/fg121/Source-Coding_WS12/selected-readings/16_T-REC-H.222.0-199507-S__PDF-E.pdf
  */
  const STREAM_Mjoll_ignore = 0x92;

  while (spanBefore(spanPos, crcSpanPos)) {
    // Rewrite stream_type to ignore some audio streams
    const pokePos = spanList.offsets[spanPos.spanIndex] + spanPos.byteOffset;
    // Skip past stream_type
    const stream_type = bytes[pokePos];
    if (stream_type == 0x0f /* STREAM_adts */) {
      if (audioIndex != audioStreamIndexToKeep) {
        bytes[pokePos] = STREAM_Mjoll_ignore;
      }

      ++audioIndex;
    }

    // Skip past stream_type and elementary_PID fields
    advanceSpanPos(spanList, spanPos, 3);

    // Skip descriptors for this stream described by the program map section.
    // eslint-disable-next-line no-bitwise
    const esInfoLengthBE16 = readSpanBEU16(bytes, spanList, spanPos);
    invariant(esInfoLengthBE16 !== null, 'Unexpected end of section before ES_info_length');
    // eslint-disable-next-line no-bitwise
    const ES_info_length = esInfoLengthBE16 & 0x0FFF;

    advanceSpanPos(spanList, spanPos, ES_info_length);
  }

  return audioIndex;
}

